import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { ArrowRight, Search, Menu, X, Columns3 } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { Logo } from "@/components/brand/Logo";
import { useCompare } from "@/context/CompareContext";
import { SearchDialog } from "@/components/layout/SearchDialog";

const NAV = [
  { to: "/brokers", label: "Find a broker", id: "nav-brokers" },
  { to: "/swaps", label: "Compare swaps", id: "nav-swaps" },
  { to: "/signals", label: "Trading signals", id: "nav-signals" },
  { to: "/articles", label: "Articles", id: "nav-articles" },
  { to: "/offers", label: "Offers", id: "nav-offers" },
  { to: "/about", label: "About us", id: "nav-about" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState(false);
  const { slugs } = useCompare();
  const { pathname } = useLocation();

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 12);
    fn(); window.addEventListener("scroll", fn, { passive: true });
    return () => window.removeEventListener("scroll", fn);
  }, []);
  useEffect(() => setOpen(false), [pathname]);
  useEffect(() => {
    const onKey = (e) => { if ((e.metaKey || e.ctrlKey) && e.key === "k") { e.preventDefault(); setSearch(true); } };
    window.addEventListener("keydown", onKey); return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <>
      <header className="fixed inset-x-0 top-0 z-50 px-4 pt-4 sm:px-6 sm:pt-5" data-testid="navbar">
        <div className={`container-x flex h-[72px] items-center justify-between rounded-full px-3 sm:px-7 transition-[background-color,border-color,box-shadow] duration-500 ${scrolled ? "bg-white/80 backdrop-blur-xl border border-[var(--line)] shadow-[0_10px_40px_-12px_rgba(10,15,29,0.18)]" : "bg-white/60 backdrop-blur-xl border border-[var(--line)] shadow-[0_8px_30px_-14px_rgba(10,15,29,0.12)]"}`}>
          <Logo />
          <nav className="hidden xl:flex items-center gap-1" aria-label="Primary">
            {NAV.map((n) => (
              <NavLink key={n.to} to={n.to} data-testid={n.id} className={({ isActive }) => `relative rounded-full px-3 h-10 inline-flex items-center whitespace-nowrap text-sm transition-colors ${isActive ? "text-[var(--ink)]" : "text-[var(--ink-3)] hover:text-[var(--ink)]"}`}>
                {({ isActive }) => (<>
                  {n.label}
                  {isActive && <motion.span layoutId="nav-dot" className="absolute -bottom-0.5 left-1/2 h-1 w-1 -translate-x-1/2 rounded-full bg-[var(--cobalt)]" />}
                </>)}
              </NavLink>
            ))}
          </nav>
          <div className="flex items-center gap-2">
            <button onClick={() => setSearch(true)} aria-label="Search site" title="Search site" data-testid="nav-search-button" className="hidden sm:inline-flex h-10 items-center gap-2 rounded-full border border-[var(--line)] bg-white/70 px-3 text-sm text-[var(--ink-3)] hover:text-[var(--ink)] hover:border-[var(--line-2)] transition-colors">
              <Search size={15} /><span className="hidden md:inline xl:hidden">Search</span><kbd className="hidden md:inline xl:hidden font-mono text-[10px] rounded border border-[var(--line)] px-1.5 py-0.5 bg-[var(--canvas)]">⌘K</kbd>
            </button>
            <Link to="/brokers/compare" data-testid="nav-compare-link" className={`relative hidden sm:inline-flex h-10 w-10 items-center justify-center rounded-full border transition-colors ${slugs.length ? "border-[var(--cobalt-line)] bg-[var(--cobalt-soft)] text-[var(--cobalt)]" : "border-[var(--line)] bg-white/70 text-[var(--ink-3)] hover:text-[var(--ink)]"}`} aria-label="Compare brokers">
              <Columns3 size={16} />
              {slugs.length > 0 && <span className="absolute -top-1 -right-1 h-4 min-w-4 rounded-full bg-[var(--cobalt)] px-1 text-[10px] font-mono text-white flex items-center justify-center" data-testid="nav-compare-count">{slugs.length}</span>}
            </Link>
            <Link to="/brokers/finder" data-testid="nav-get-matched" className="btn-primary h-10 px-5 text-[13px] hidden sm:inline-flex">Get matched <ArrowRight size={15} /></Link>
            <button onClick={() => setOpen(!open)} data-testid="nav-mobile-toggle" className="xl:hidden inline-flex h-10 w-10 items-center justify-center rounded-full border border-[var(--line)] bg-white/70" aria-label="Menu" aria-expanded={open}>
              {open ? <X size={18} /> : <Menu size={18} />}
            </button>
          </div>
        </div>
        <AnimatePresence>
          {open && (
            <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }} className="xl:hidden overflow-hidden border-t border-[var(--line)] bg-white/95 backdrop-blur-xl" data-testid="mobile-menu">
              <div className="container-x py-4 flex flex-col gap-1">
                {NAV.map((n) => <NavLink key={n.to} to={n.to} data-testid={`${n.id}-mobile`} className="rounded-lg px-3 h-11 flex items-center text-base text-[var(--ink)] hover:bg-[var(--canvas)]">{n.label}</NavLink>)}
                <Link to="/brokers/compare" className="rounded-lg px-3 h-11 flex items-center text-base text-[var(--ink)] hover:bg-[var(--canvas)]">Compare ({slugs.length})</Link>
                <Link to="/brokers/finder" className="btn-primary mt-2">Get matched <ArrowRight size={15} /></Link>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </header>
      <SearchDialog open={search} onOpenChange={setSearch} />
    </>
  );
};
